/**
 * LoginPage — Sparta admin first-render screen for unauthenticated users.
 *
 * SCRUM-57 Variant A desktop layout (image-left / form-right `1.1fr 1fr`
 * grid) + mobile hero strip (140 px) preserved. SCRUM-73 drops the
 * FE-only portal selector — portal selection moves post-auth to /portal
 * (SCRUM-113, next on this branch). The form submits credentials only;
 * `loginSchema` always emitted `{ email, password }` so no validator
 * change is required.
 *
 * Copy aligned with `.design-incoming/auth-admin-20260522/COPY.md`
 * (subhead "Sign in to continue.", primary button "Sign in", error
 * table for invalid_credentials / account_locked / rate_limited /
 * service_unavailable / network_error / unknown). The error region
 * was moved inline ABOVE the email field per the bundle's
 * `ACCESSIBILITY.md §2` ARIA pattern.
 *
 * Error region: inline `<Badge variant="danger">` above the email
 * field, per bundle spec. The kit Badge (SCRUM-115, PR #143) is on
 * main as of merge `0521a46`; this surface consumes it directly.
 * The Badge sits inside the `role="alert"` wrapper — ACCESSIBILITY.md
 * §10 explicitly forbids pairing `aria-live` with `role="alert"`, so
 * the wrapper carries the alert semantics and the Badge carries the
 * visual treatment.
 *
 * Visible focus rings, ≥ 44 px touch targets, semantic labels, and
 * `role="alert"` error feedback are present from day one (CLAUDE.md
 * FE rule §7). Tokens are sourced exclusively through the Tailwind preset
 * (`@sparta/design-tokens`) and the semantic CSS variables declared in
 * `apps/admin/src/index.css` (`--bg-canvas`, `--fg-primary`, …).
 */

import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  loginSchema,
  type LoginInput,
  LoginResponseSchema,
} from '@artemis/validators';
import { Badge, BrandMark, Button, Icon, TextField } from '@sparta/ui-kit';
import { apiClient } from '../../shared/api-client.js';
import { MfaChallengePanel } from './MfaChallengePanel.js';
import { MfaEnrollmentPanel } from './MfaEnrollmentPanel.js';

/**
 * State carried while the user is mid-MFA after a successful first-factor
 * login. Both branches require their respective short-lived JWT to be
 * preserved across the form-replacement render — `mfaToken` is the
 * credential for `POST /auth/login/mfa`, `enrollmentToken` is the
 * credential for the three `POST /auth/mfa/enroll/*` endpoints (start,
 * confirm, complete). The actual flow lives in `MfaChallengePanel` and
 * `MfaEnrollmentPanel`; LoginPage only keeps the discriminator state.
 */
type MfaPendingState =
  | { kind: 'required'; mfaToken: string }
  | { kind: 'enrollment_required'; enrollmentToken: string };

// SCRUM-57: placeholder hero — real photography is a follow-up.
// Portrait composition (viewBox 600x800) so the tall hero column at desktop
// (≈ 622×752 inside the 24px padding) renders without significant horizontal
// cropping. Mirrors the handoff `ScenicPlaceholder kind="coast"` palette
// (`#dfe4e6 → #bdc3c7 → #84a0b6 → #34495e`) and composition (sky gradient,
// soft sun in the upper right, two undulating coast layers near the bottom)
// — re-pathed for the portrait viewBox so the sun and water shapes stay
// fully in-frame at the column's true aspect ratio.
function HeroPanel({ className }: { className?: string }): React.ReactElement {
  return (
    <div
      aria-hidden="true"
      className={className}
      style={{ background: '#dfe4e6' }}
    >
      <svg
        viewBox="0 0 600 800"
        preserveAspectRatio="xMidYMid slice"
        className="h-full w-full"
        focusable="false"
      >
        <defs>
          <linearGradient id="hero-coast-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#dfe4e6" />
            <stop offset="100%" stopColor="#bdc3c7" />
          </linearGradient>
        </defs>
        {/* Sky gradient covers the full canvas; coast layers paint over it. */}
        <rect width="600" height="800" fill="url(#hero-coast-sky)" />
        {/* Soft sun — placed in the upper-right third, well within the
            visible window. r=58 matches the handoff token. */}
        <circle cx="470" cy="200" r="58" fill="#bdc3c7" opacity="0.75" />
        {/* Distant water layer — undulating curve at ~y=520 sweeping across
            the full 600 width so the horizon line hits both edges of the
            column (no flat floating shape). */}
        <path
          d="M0,520 Q150,470 300,510 T600,495 L600,800 L0,800 Z"
          fill="#84a0b6"
          opacity="0.85"
        />
        {/* Foreground water layer — a second curve at ~y=600, deeper navy. */}
        <path
          d="M0,600 Q150,560 300,590 T600,575 L600,800 L0,800 Z"
          fill="#34495e"
        />
      </svg>
    </div>
  );
}

// SCRUM-102: the local `function BrandMark` that previously lived here
// (token-driven fills, 32 px hardcoded, slightly different inner-D
// geometry — likely a SCRUM-57-era hand-roll) was absorbed into the
// kit BrandMark. The call site below now passes `size={32}` to match
// the previous visual size. The fill shifts from `var(--fg-brand)`
// (= `#2c3e50` in light mode) to the dashboard handoff's canonical
// `#34495e` — about 2.5% darker — and the inner D-shape geometry
// shifts slightly. Forward-looking note: if the auth handoff bundle
// returns and says the token-driven fill is canonical, the kit
// BrandMark gets a `tint?` prop (or theme-aware variant) at that
// point. See SCRUM-102 ticket.

const ARROW_RIGHT = <Icon name="arrowRight" size={18} />;

// SCRUM-100: MAIL_ICON and LOCK_ICON local SVG constants were removed
// when the email + password fields adopted the kit TextField's
// `icon="mail" | "lock"` prop (which resolves to the kit Icon
// dictionary's mail / lock glyphs, ported from the dashboard handoff
// via SCRUM-97). The kit Icon and the previous local SVGs share the
// same visual contract (24-viewBox, currentColor stroke, 1.75 weight,
// round line-cap / line-join).

const MAX_EMAIL_LENGTH = 254;

export function LoginPage(): React.ReactElement {
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [mfaPending, setMfaPending] = React.useState<MfaPendingState | null>(
    null,
  );
  const [capsLockOn, setCapsLockOn] = React.useState(false);

  const {
    register,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    mode: 'onTouched',
  });

  const onPasswordKey = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      try {
        setCapsLockOn(event.getModifierState('CapsLock'));
      } catch {
        /* getModifierState may not exist on some test events */
      }
    },
    [],
  );

  const onSubmit = handleSubmit(async (values): Promise<void> => {
    setSubmitError(null);
    setMfaPending(null);

    // Form-level email length cap layered on top of the SSOT loginSchema.
    // The Zod email regex is RFC-pattern based and does not enforce the
    // RFC 5321 254-char total length. We do not redefine the schema —
    // we surface this as a field error via setError so the existing
    // aria-invalid / aria-describedby plumbing handles it.
    if (values.email.length > MAX_EMAIL_LENGTH) {
      setError('email', { message: 'Invalid email — too long.' });
      return;
    }

    try {
      // Route through the shared apiClient so the CSRF middleware
      // fetches and attaches the `x-csrf-token` header. POST /auth/login
      // is @Public() but NOT @CsrfExempt() — CSRF still required
      // (apps/api/src/common/guards/csrf.guard.ts L41-43).
      const { data, response } = await apiClient.POST('/auth/login', {
        body: values,
      });
      if (!response.ok) {
        // Copy aligned with auth-admin-20260522/COPY.md "Variant A /
        // Mobile error states" table. 403/422 don't have COPY.md keys
        // (CSRF + field-validation fall-throughs) so they keep their
        // SCRUM-57 copy. The 423 wording omits the {minutes} ICU plural
        // because the backend does not yet surface a retry-after value;
        // once it does, swap to ICU ("Try again in {n, plural, …}.") per
        // COPY.md §i18n notes.
        if (response.status === 401) {
          setSubmitError('Email or password is incorrect.');
          setFocus('email');
        } else if (response.status === 403) {
          setSubmitError('Sign-in blocked. Refresh the page and try again.');
        } else if (response.status === 422) {
          setSubmitError('Please check the email and password fields.');
        } else if (response.status === 423) {
          setSubmitError('Too many tries. Account locked. Try again shortly.');
        } else if (response.status === 429) {
          setSubmitError('Too many tries. Try again in a moment.');
        } else if (response.status >= 500) {
          setSubmitError('Sign-in is temporarily unavailable. Try again shortly.');
        } else {
          setSubmitError('Something went wrong. Try again.');
        }
        return;
      }

      // Discriminated 200 — POST /auth/login returns one of three
      // variants (see packages/validators/src/auth.mfa.ts and
      // apps/api/src/modules/identity/interface/dto/auth-login.response.dto.ts):
      //   - { status: 'authenticated', user }              → session cookie set; safe to redirect
      //   - { status: 'mfa_required', mfaToken, expiresAt } → no cookie; need MFA verification
      //   - { status: 'mfa_enrollment_required', enrollmentToken, expiresAt }
      //                                                    → no cookie; need MFA enrollment
      //
      // Treating any 2xx as "authenticated" would redirect the user to
      // `/` for the MFA branches and bounce them straight back to the
      // unauthenticated shell. We runtime-validate the body with the
      // shared discriminator schema and branch explicitly.
      const parsed = LoginResponseSchema.safeParse(data);
      if (!parsed.success) {
        // Maps to COPY.md `unknown` row — discriminator-mismatch is an
        // implementation surprise, not a user-actionable state.
        setSubmitError('Something went wrong. Try again.');
        return;
      }
      if (parsed.data.status === 'authenticated') {
        window.location.assign('/');
        return;
      }
      if (parsed.data.status === 'mfa_required') {
        setMfaPending({
          kind: 'required',
          mfaToken: parsed.data.mfaToken,
        });
        return;
      }
      if (parsed.data.status === 'mfa_enrollment_required') {
        setMfaPending({
          kind: 'enrollment_required',
          enrollmentToken: parsed.data.enrollmentToken,
        });
        return;
      }
    } catch {
      setSubmitError("Couldn't reach the server. Check your connection.");
    }
  });

  return (
    <main
      className="min-h-screen w-full"
      style={{ background: 'var(--bg-canvas)', color: 'var(--fg-primary)' }}
    >
      {/* Grid pinned to 100vh at lg+ with `grid-rows-[100vh]` so the
          grid TRACK (not just the container) is the viewport height —
          otherwise CSS Grid sizes the row to the tallest column's
          content (form ~870px), which stretches the hero column too
          and pushes the bottom-anchored overlay below the fold on
          taller monitors. `min-h-0` on each column lets them shrink
          to the row height; `overflow-y-auto` on the form column
          handles the rare case where form content exceeds viewport. */}
      <div className="grid min-h-screen grid-cols-1 lg:h-screen lg:grid-cols-[1.1fr_1fr] lg:grid-rows-[100vh]">
        {/* Mobile hero strip */}
        <div className="relative h-[140px] w-full overflow-hidden lg:hidden">
          <HeroPanel className="absolute inset-0" />
        </div>
        {/* Desktop hero column — handoff §"Variant A — Desktop": 24px
            padding around the ScenicPlaceholder; absolute-positioned overlay
            at left:48 bottom:56 max-width:380 (positions relative to the
            column wrapper, not the placeholder, so 24px of the offset is
            the column padding and the rest sits inside the placeholder). */}
        <div className="relative hidden p-6 lg:block lg:h-full lg:min-h-0">
          <HeroPanel className="relative h-full w-full overflow-hidden rounded-2xl" />
          <div
            className="pointer-events-none absolute"
            style={{
              left: '48px',
              bottom: '56px',
              maxWidth: '380px',
              color: '#ffffff',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
            }}
          >
            <p
              className="font-bold uppercase"
              style={{
                fontSize: '12px',
                letterSpacing: '1.4px',
                opacity: 0.85,
                marginBottom: '12px',
              }}
            >
              Desata · v0.1
            </p>
            <p
              className="font-bold"
              style={{
                fontSize: '36px',
                lineHeight: 1.15,
                letterSpacing: '-1px',
                margin: 0,
              }}
            >
              The operating surface for your short-term rental business.
            </p>
          </div>
        </div>

        {/* Form column — pinned to 100vh at lg+ with `min-h-0` so the
            grid track (set to 100vh above) actually constrains the
            column's height, and `overflow-y-auto` so the form scrolls
            internally if its content exceeds the viewport.
            `safe center` is the CSS keyword that centers the form
            panel vertically when it fits, but top-anchors it when
            content overflows — without `safe`, the brand bar +
            "Welcome back" get clipped at the top of the scroll
            area on short viewports. */}
        <div className="flex items-center justify-center px-4 py-10 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:p-12 lg:[align-items:safe_center]">
          <div
            className="w-full max-w-[420px]"
            style={{ color: 'var(--fg-primary)' }}
          >
            {/* Brand bar — handoff: BrandMark 32×32 + "Desata" wordmark
                20px/700/letter-spacing -.3, gap 10px, margin-bottom 8px. */}
            <div className="mb-2 flex items-center gap-[10px]">
              <BrandMark size={32} />
              <span
                className="text-xl font-bold"
                style={{ letterSpacing: '-0.3px' }}
              >
                Desata
              </span>
            </div>

            {/* Heading — handoff: 28px/700/letter-spacing -.6, margin "12px 0 4px".
                `id="signin-heading"` paired with the form's
                `aria-labelledby="signin-heading"` below per the
                auth-admin-20260522 ACCESSIBILITY.md §2 ARIA pattern. */}
            <h1
              id="signin-heading"
              className="mt-3 font-bold"
              style={{ fontSize: '28px', letterSpacing: '-0.6px' }}
            >
              Welcome back
            </h1>

            {/* Subhead — SCRUM-73 aligned with COPY.md "Sign in to continue."
                The SCRUM-57 portal-mention ("You're entering the {portal}
                portal.") was dropped along with the form's portal selector
                because portal selection now happens post-auth at /portal
                (SCRUM-113). */}
            <p
              className="mb-6 mt-1 text-sm"
              style={{ color: 'var(--fg-tertiary)' }}
            >
              Sign in to continue.
            </p>

            {mfaPending === null ? (
              <form
                onSubmit={onSubmit}
                noValidate
                aria-busy={isSubmitting}
                aria-labelledby="signin-heading"
              >
              {/* Above-email error region — SCRUM-73 moved this above the
                  email field per the auth-admin-20260522 ACCESSIBILITY.md
                  §2 ARIA pattern (`<form aria-labelledby="signin-heading">`
                  with the error inline above the first field).

                  Bundle's ACCESSIBILITY.md §5 + §10: form errors announce
                  via `role="alert"` (assertive). Explicitly DO NOT add
                  `aria-live` on top — §10 "Don't `aria-live` the form
                  errors — use `role="alert"` instead, more reliable
                  across screen readers." The wrapper carries the alert
                  semantics; the kit `<Badge variant="danger">` (SCRUM-115)
                  carries the visual treatment. Pass 2 swap landed in
                  Sergey round-2 (2026-05-28) after PR #143 merged onto
                  this branch via `0521a46`. */}
              <div role="alert" className="mb-3 min-h-5">
                {submitError !== null ? (
                  <Badge variant="danger">{submitError}</Badge>
                ) : null}
              </div>

              {/* Email field — handoff Field primitive: label 13/600/fg-primary,
                  Field margin-bottom 16, label-to-input gap mb-2 (8px).
                  ───────────────────────────────────────────────────────
                  INTENTIONAL VISUAL-FIDELITY DEVIATION — DO NOT REVERT
                  ───────────────────────────────────────────────────────
                  Sketched nominal:   Input height = 44px (per the login-surface
                                       Sketch dated 2026-05-19; canonical contract
                                       now in packages/ui-kit/src/TextField.docs.mdx).
                  This screen uses:   Input height = 56px (`h-14` below).
                  Reason:             Visual fidelity at the actual desktop
                                       review frame (spec 1280×760). At that
                                       frame the form panel renders at the
                                       spec's max-width: 420px and a 44px
                                       input reads as 9.55:1 — perceptibly
                                       thin. The handoff `preview.html`
                                       ships artboards at 724×983 where the
                                       same 44px input renders inside a
                                       249px-wide form panel (5.66:1) — the
                                       proportion the design preview shows.
                                       56px reconciles the live page's
                                       proportion with what the preview
                                       implies the designer intended.
                  Scope:              Login surface only — deliberate,
                                       login-screen-specific decision
                                       reviewed and approved by the repo
                                       owner. NOT a bug, NOT an accidental
                                       drift from the design tokens.
                  Standing rule:      `feedback_sparta_v1_frontend_visual_
                                       fidelity_dod.md` ("Lesson — design
                                       handoff `preview.html` files render
                                       artboards at scaled sizes…"). */}
              {/*
                SCRUM-100: email field composes the kit TextField
                (size="lg" preserves the SCRUM-57 56 px login-surface
                deviation documented in the comment block above;
                icon="mail" supplies the leading-icon prefix; kit owns
                aria-invalid + aria-describedby + error paragraph).

                Note on type="text" + inputMode="email": kept as-is.
                JSDOM normalises `<input type=email>` values by
                stripping trailing whitespace, which would silently
                pass an adversarial trailing-space email through Zod
                validation. Real-browser UX is unaffected — the
                mobile keyboard still surfaces the email layout via
                `inputMode`, and Zod's regex is the single source of
                truth for email format.
              */}
              <TextField
                label="Work email"
                type="text"
                autoComplete="username"
                inputMode="email"
                spellCheck={false}
                autoCapitalize="off"
                placeholder="you@company.com"
                disabled={isSubmitting}
                icon="mail"
                size="lg"
                error={errors.email?.message}
                {...register('email')}
              />

              {/* Password field — same Field spec as email field above,
                  including the same intentional 56px input-height
                  deviation (handoff nominal 44px → login surface 56px,
                  canonical `h-14`). See the email Field's comment block
                  for the full rationale.

                  SCRUM-100: composes the kit TextField (size="lg",
                  icon="lock"). The caps-lock hint flows through
                  TextField's `hint` slot — a generic non-error
                  helper-paragraph slot — so the existing UX (paragraph
                  appears between input and error when caps-lock is on,
                  with aria-describedby wired automatically) is
                  preserved. The Zod `too_small` → "Password is
                  required." mapping stays at this call site to honor
                  CLAUDE.md FE rule §4 (the schema is the single source
                  of truth; product copy maps locally). */}
              <TextField
                label="Password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                disabled={isSubmitting}
                icon="lock"
                size="lg"
                onKeyDown={onPasswordKey}
                onKeyUp={onPasswordKey}
                hint={capsLockOn ? 'Caps Lock is on.' : undefined}
                error={
                  errors.password !== undefined
                    ? errors.password.type === 'too_small'
                      ? 'Password is required.'
                      : errors.password.message
                    : undefined
                }
                {...register('password')}
              />

              {/* Stay signed in + Forgot password — handoff: margin "4px 0 20px".
                  The password Field's mb-4 (16) + this row's mt-1 (4) sums
                  to the 20px Field-to-row gap before the row, then mb-5
                  (20) below. */}
              <div className="mb-5 mt-1 flex items-center justify-between">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    name="remember"
                    defaultChecked
                    className="h-4 w-4"
                    style={{ accentColor: '#34495e' }}
                  />
                  <span style={{ color: 'var(--fg-secondary)' }}>
                    Stay signed in
                  </span>
                </label>
                <a
                  href="/password-reset"
                  className="text-[13px] font-semibold no-underline"
                  style={{ color: 'var(--fg-brand)' }}
                >
                  Forgot password?
                </a>
              </div>

              {/* Primary submit — SCRUM-73 aligned with COPY.md "Sign in".
                  aria-label still wins on the loading branch so SR reads
                  "Signing in" instead of the visible "Signing in…". */}
              {/* SCRUM-101: composes the kit Button (variant="primary",
                  size="lg", full). The kit's `--bg-brand` token
                  resolves to the same brand-navy in light mode; the
                  previous inline-style block (background/color +
                  boxShadow) is now kit-managed. The spinner SVG +
                  arrow icon stay as call-site children (loading-state
                  UX is a stateful consumer decision). */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                full
                disabled={isSubmitting}
                aria-busy={isSubmitting}
                aria-disabled={isSubmitting}
                aria-label={isSubmitting ? 'Signing in' : undefined}
              >
                {isSubmitting ? (
                  <>
                    {/* SCRUM-199: loading spinner now uses the kit Icon
                        dictionary (`loader` → Lucide Loader) with
                        `animate-spin`. Stroke follows the Button's white
                        currentColor (no hardcoded hex; design-token lint). */}
                    <Icon name="loader" size={18} className="animate-spin" />
                    <span aria-hidden="true">Signing in…</span>
                  </>
                ) : (
                  <>
                    <span>Sign in</span>
                    {ARROW_RIGHT}
                  </>
                )}
              </Button>

              {/* Divider */}
              <div
                className="my-5 flex items-center gap-3 text-xs"
                style={{ color: 'var(--fg-tertiary)' }}
              >
                <span
                  className="h-px flex-1"
                  style={{ background: 'var(--border-subtle)' }}
                />
                <span>or</span>
                <span
                  className="h-px flex-1"
                  style={{ background: 'var(--border-subtle)' }}
                />
              </div>

              {/* SSO secondary button — disabled placeholder.
                  SCRUM-57: backend SSO prerequisite not shipped — see plan
                  §"Stop-and-ask carve-outs". No onClick handler; click does
                  not submit the form (button type="button"). */}
              <button
                type="button"
                disabled
                aria-disabled="true"
                title="SSO coming soon"
                /* Handoff Btn lg: h 48, font 16/600. */
                className="flex h-12 w-full items-center justify-center gap-2 rounded-full border text-[16px] font-semibold"
                style={{
                  background: 'var(--bg-surface)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--fg-disabled)',
                  cursor: 'not-allowed',
                }}
              >
                Continue with SSO
              </button>

              {/* Footer */}
              <p
                className="mt-6 text-center text-xs"
                style={{ color: 'var(--fg-tertiary)' }}
              >
                Protected by reCAPTCHA · Privacy · Terms
              </p>
            </form>
            ) : mfaPending.kind === 'required' ? (
              <MfaChallengePanel
                mfaToken={mfaPending.mfaToken}
                onSuccess={() => window.location.assign('/')}
                onCancel={() => setMfaPending(null)}
              />
            ) : (
              <MfaEnrollmentPanel
                enrollmentToken={mfaPending.enrollmentToken}
                onSuccess={() => window.location.assign('/')}
                onCancel={() => setMfaPending(null)}
              />
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
